// ============================================================
// ClientInfoSections.tsx
// The REUSABLE "client info" block — Client Details tab redesign (approved
// mockup casevo-client-details-view-edit.html, Option C · Hybrid):
//   • VIEW (default): surface cards of labelled values, an at-a-glance strip
//   • PER-SECTION edit: a card's pencil → that card's inputs + Cancel/Save
//   • EDIT ALL: every card into inputs at once + one Save/Cancel (header +
//     a sticky save bar)
// Every card comes from CLIENT_INFO_SECTIONS (clientInfoRegistry.tsx).
//
// SAVE MODEL — unchanged from the old form: inputs write straight through
// `onChange` (→ the store → the existing debounced autosave + live-sync /
// field presence), so collaborators see edits as before and nothing new
// can be lost. On top of that:
//   Save   → validate that section (or all) → `onFlush()` (persist now) →
//            back to view.
//   Cancel → restore the values captured when editing began (one onChange)
//            and broadcast the restored fields — the autosave persists the
//            revert. Both paths share the same underlying save.
//
// REUSE — the component knows nothing about where the person lives:
//   client page: person = selectedClient,         prefix 'client',
//                onChange = updateSelectedClient,  onFlush = flushClientAutoSave
//   case page:   person = currentCase.clientDetails, prefix 'clientDetails',
//                onChange = snapshot updater,      onFlush = flushAutoSave,
//                strict  = true (case validation rules)
// ============================================================
import React, { useMemo, useState } from 'react'
import CardPresence from '../shared/CardPresence'
import PhoneLink from '../shared/PhoneLink'
import { flushLiveDocSnapshots, saveDidFail, SAVE_FAILED_MSG } from '../shared/liveDocFlush'
import { validatePerson } from '../../utils/validation'
import { useFieldFocusRequest } from '../../utils/fieldFocusRequest'
import {
  CLIENT_INFO_SECTIONS, ALL_PERSON_FIELDS, IcEdit, IcCheck, IcPhone, IcMail, IcCal, IcPin, ageOf, formatDob,
  type ClientInfoPerson, type ClientInfoSectionDef, type ClientInfoSectionKey, type PersonField, type SectionCtx,
} from './clientInfoRegistry'

// ── mockup classes (exact values) ──
export const CARD =
  'bg-casevo-surface border border-casevo-border rounded-[14px] ' +
  'shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
export const CARD_EDITING =
  // `!` — must beat CARD's own shadow utility (same specificity; CSS order
  // decided it, so the 3px editing ring never showed).
  '!border-casevo-mid !shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] ' +
  'dark:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
export const BTN = 'inline-flex items-center gap-[7px] h-9 px-[14px] rounded-[10px] text-[13.5px] font-medium border whitespace-nowrap transition-colors focus:outline-none casevo-focus disabled:opacity-45 disabled:cursor-not-allowed'
export const BTN_SEC = `${BTN} border-casevo-border bg-casevo-surface text-casevo-forest enabled:hover:border-casevo-mid`
export const BTN_PRI = `${BTN} border-casevo-primary bg-casevo-primary text-white font-semibold enabled:hover:bg-casevo-primary-hover`
export const SM = '!h-[30px] !px-[11px] !text-[12.5px] !rounded-[8px]'

// Address-search boxes carry their own presence key (clientInfoRegistry
// SEARCH_SLUG) — counted as part of their card for the header presence.
const SEARCH_KEY: Partial<Record<ClientInfoSectionKey, string>> = {
  address: 'address-search', gp: 'gp-address-search', hospital: 'hospital-address-search',
}

export interface ClientInfoSectionsProps {
  person: ClientInfoPerson
  onChange: (changes: Partial<ClientInfoPerson>) => void
  // Presence/live-sync fieldKey namespace: 'client' | 'clientDetails'.
  fieldKeyPrefix: string
  // Broadcast programmatic multi-field changes (address lookup, Cancel).
  onBroadcast?: (fieldKeys: string[]) => void
  // Persist pending edits now (Save). Optional — autosave runs regardless.
  onFlush?: () => Promise<void>
  // A card's (or the whole page's) Cancel has put the values back.
  onCancel?: () => void
  // Case rules (forename, surname, DOB, NI, phone, email required).
  strict?: boolean
  sections?: ClientInfoSectionKey[]
  title?: string
  subtitle?: React.ReactNode
  // Footer hint on an editing card ("Saves to Sarah's client record").
  saveHint?: string
  showGlance?: boolean
  // Extra cards rendered after the registry cards, inside the same grid.
  children?: React.ReactNode
  // Case page (Stage 2): group eyebrow + status chip above the title, a slot
  // between the header and the glance strip (linked-client strip + outdated
  // banner), per-field outdated markers, and the section footer.
  eyebrow?: React.ReactNode
  topSlot?: React.ReactNode
  outdated?: SectionCtx['outdated']
  footer?: React.ReactNode
  // (B2c) Case page: the case's protected-party flag (litigation friend label).
  protectedParty?: boolean
}

const ClientInfoSections: React.FC<ClientInfoSectionsProps> = ({
  person, onChange, fieldKeyPrefix, onBroadcast, onFlush, onCancel, strict = false,
  sections, title = 'Client details', subtitle, saveHint, showGlance = true, children,
  eyebrow, topSlot, outdated, footer, protectedParty,
}) => {
  // Case-only cards (On this case) appear only when `sections` asks for them.
  const sectionsKey = sections?.join(',')
  const defs = useMemo<ClientInfoSectionDef[]>(
    () => (sections ? CLIENT_INFO_SECTIONS.filter((s) => sections.includes(s.key)) : CLIENT_INFO_SECTIONS.filter((s) => !s.caseOnly)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sectionsKey],
  )
  // editing: per-section open set; all: whole-page edit. snapshot: values
  // captured when editing began (Cancel restores them). attempted: sections
  // whose Save was pressed → show their validation errors.
  const [editing, setEditing] = useState<Set<ClientInfoSectionKey>>(new Set())
  const [all, setAll] = useState(false)
  // ★ PER-CARD snapshots (case-page audit): cards can share fields (On this
  // case carries the GP/Hospital fields too), so one shared snapshot let a
  // second card's open overwrite the first card's Cancel values.
  const [snapshots, setSnapshots] = useState<Partial<Record<ClientInfoSectionKey | 'all', Partial<ClientInfoPerson>>>>({})
  const [attempted, setAttempted] = useState<Set<ClientInfoSectionKey | 'all'>>(new Set())
  const [saving, setSaving] = useState<ClientInfoSectionKey | 'all' | null>(null)
  // Section (or 'all') whose last Save failed → stays open with a message.
  const [failed, setFailed] = useState<ClientInfoSectionKey | 'all' | null>(null)

  const errors = useMemo(() => validatePerson(person, { strict }), [person, strict])
  const errFor = (f: PersonField): string | undefined => errors.find((e) => e.field === f)?.message
  const sectionErrors = (fields: PersonField[]): boolean => errors.some((e) => fields.includes(e.field as PersonField))
  const REQUIRED: PersonField[] = strict
    ? ['forename', 'surname', 'dateOfBirth', 'niNumber', 'mainContactNumber', 'emailAddress']
    : ['forename', 'surname']

  const fk = (f: string): string => `${fieldKeyPrefix}.${f}`
  const capture = (fields: PersonField[]): Partial<ClientInfoPerson> =>
    Object.fromEntries(fields.map((f) => [f, person[f]])) as Partial<ClientInfoPerson>
  const restore = (fields: PersonField[], snapshot: Partial<ClientInfoPerson> = {}): void => {
    const changed = fields.filter((f) => snapshot[f] !== person[f] && f in snapshot)
    if (changed.length === 0) return
    onChange(Object.fromEntries(changed.map((f) => [f, snapshot[f]])) as Partial<ClientInfoPerson>)
    onBroadcast?.(changed.map(fk))
  }

  const open = (def: ClientInfoSectionDef): void => {
    setSnapshots((s) => ({ ...s, [def.key]: capture(def.fields) }))
    setEditing((e) => new Set(e).add(def.key))
  }
  const close = (key: ClientInfoSectionKey): void => {
    setFailed((f) => (f === key ? null : f))
    setEditing((e) => { const n = new Set(e); n.delete(key); return n })
    setAttempted((a) => { const n = new Set(a); n.delete(key); return n })
  }
  const save = async (def: ClientInfoSectionDef): Promise<void> => {
    if (sectionErrors(def.fields)) { setAttempted((a) => new Set(a).add(def.key)); return }
    setSaving(def.key)
    setFailed(null)
    try { await flushLiveDocSnapshots(); await onFlush?.() } finally { setSaving(null) }
    if (saveDidFail()) { setFailed(def.key); return }
    close(def.key)
  }
  const cancel = (def: ClientInfoSectionDef): void => { restore(def.fields, snapshots[def.key]); close(def.key); onCancel?.() }

  const allFields = useMemo(() => defs.flatMap((d) => d.fields), [defs])
  const editAll = (): void => { setSnapshots((s) => ({ ...s, all: capture(ALL_PERSON_FIELDS) })); setAttempted(new Set()); setAll(true) }
  const saveAll = async (): Promise<void> => {
    if (sectionErrors(allFields)) { setAttempted(new Set<ClientInfoSectionKey | 'all'>(['all'])); return }
    setSaving('all')
    setFailed(null)
    try { await flushLiveDocSnapshots(); await onFlush?.() } finally { setSaving(null) }
    if (saveDidFail()) { setFailed('all'); return }
    setAll(false); setAttempted(new Set())
  }
  const cancelAll = (): void => { restore(allFields, snapshots.all); setAll(false); setAttempted(new Set()); setFailed(null); onCancel?.() }

  const anyOpen = editing.size > 0
  // "Take me to that field" (the case page's required-fields card) — only the
  // case copy (clientDetails.*) answers; the client record's page never does.
  useFieldFocusRequest(
    (field) => (fieldKeyPrefix === 'clientDetails' ? defs.find((d) => (d.fields as string[]).includes(field))?.key ?? null : null),
    (key) => { const def = defs.find((d) => d.key === key); if (def && !all && !editing.has(def.key)) open(def) },
    (key) => all || editing.has(key as ClientInfoSectionKey),
    (key) => `[data-focus-card="ci-${fieldKeyPrefix}-${key}"]`,
  )
  const ctxFor = (def: ClientInfoSectionDef, isEditing: boolean): SectionCtx => {
    const show = attempted.has(def.key) || attempted.has('all')
    return {
      person,
      set: onChange,
      fk,
      broadcast: (fields) => onBroadcast?.(fields.map(fk)),
      err: (f) => (show ? errFor(f) : undefined),
      required: (f) => REQUIRED.includes(f),
      onAdd: !isEditing && !all ? () => open(def) : undefined,
      idPrefix: `ci-${fieldKeyPrefix}-${def.key}`,
      outdated,
      protectedParty,
    }
  }

  const g = person
  const dobAge = g.dateOfBirth ? ageOf(g.dateOfBirth) : null
  // Cells: 4-across ≥md (hairline between), 2×2 below (hairline grid).
  const GLANCE_BORDERS = ['border-r border-b md:border-b-0', 'border-b md:border-b-0 md:border-r', 'border-r', '']
  const glanceCell = (i: number): string => `py-[13px] px-4 min-w-0 border-casevo-border-soft ${GLANCE_BORDERS[i]}`
  const glanceLbl = 'text-[11px] font-bold tracking-[0.12em] uppercase text-casevo-text-soft flex items-center gap-1.5'
  const glanceVal = 'text-[14.5px] font-semibold mt-[3px] truncate text-casevo-forest'
  const glanceSub = 'text-[12px] text-casevo-text-soft truncate'
  const glanceLink = 'hover:text-casevo-brand focus:outline-none casevo-focus rounded-[2px]'
  const dash = <span className="text-casevo-text-soft font-normal">—</span>

  return (
    // Root sets the mockup's body defaults (14px / forest) for inherited text.
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      {/* ── section header (mockup .shead) ── */}
      <div className="flex items-end gap-3 mb-[14px] flex-wrap">
        <div className="flex-1 min-w-0">
          {eyebrow && <div className="flex items-center gap-[8px] text-[11px] tracking-[0.16em] uppercase font-bold text-casevo-text-soft">{eyebrow}</div>}
          <h2 className={`${eyebrow ? 'mt-[3px] mb-0' : 'm-0'} text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest`}>{all ? `Editing ${title.toLowerCase()}` : title}</h2>
          {subtitle && <div className="text-[13px] text-casevo-text-soft mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          {all ? (
            <>
              <button type="button" className={BTN_SEC} onClick={cancelAll} disabled={saving === 'all'}>Cancel</button>
              <button type="button" className={BTN_PRI} onClick={() => { void saveAll() }} disabled={saving === 'all'}>{IcCheck(14)}{saving === 'all' ? 'Saving…' : 'Save changes'}</button>
            </>
          ) : (
            <button type="button" className={BTN_SEC} onClick={editAll} disabled={anyOpen} title={anyOpen ? 'Finish the open section first' : undefined}>{IcEdit(15)}Edit all</button>
          )}
        </div>
      </div>

      {topSlot}

      {/* ── at-a-glance strip (Option C) ── */}
      {showGlance && !all && (
        <div className={`${CARD} grid grid-cols-2 md:grid-cols-4 overflow-hidden mb-[14px]`}>
          <div className={glanceCell(0)}>
            <div className={glanceLbl}>{IcPhone(13)}Phone</div>
            <div className={`${glanceVal} tabular-nums`}>{g.mainContactNumber.trim() ? <PhoneLink value={g.mainContactNumber} className={glanceLink} size="sm" /> : dash}</div>
            <div className={glanceSub}>Main contact</div>
          </div>
          <div className={glanceCell(1)}>
            <div className={glanceLbl}>{IcMail(13)}Email</div>
            <div className={glanceVal} title={g.emailAddress || undefined}>{g.emailAddress.trim() ? <a className={glanceLink} href={`mailto:${g.emailAddress}`}>{g.emailAddress}</a> : dash}</div>
            <div className={glanceSub}>Email address</div>
          </div>
          <div className={glanceCell(2)}>
            <div className={glanceLbl}>{IcCal(13)}Born</div>
            <div className={`${glanceVal} tabular-nums`}>{g.dateOfBirth ? formatDob(g.dateOfBirth) : dash}</div>
            <div className={glanceSub}>{dobAge !== null ? `Age ${dobAge}` : 'Date of birth'}</div>
          </div>
          <div className={glanceCell(3)}>
            <div className={glanceLbl}>{IcPin(13)}Lives in</div>
            <div className={glanceVal}>
              {g.addressTown.trim() || g.addressPostcode.trim()
                ? <>{g.addressTown}{g.addressTown.trim() && g.addressPostcode.trim() ? ' · ' : ''}<span className="font-mono text-[13px]">{g.addressPostcode}</span></>
                : dash}
            </div>
            <div className={glanceSub}>{g.addressLine1 || 'Home address'}</div>
          </div>
        </div>
      )}

      {/* ── the cards ── */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-[14px] ${anyOpen ? 'items-start' : 'items-stretch'}`}>
        {defs.map((def) => {
          const isEditing = all || editing.has(def.key)
          const solo = isEditing && !all
          const ctx = ctxFor(def, isEditing)
          const Body = isEditing ? def.Edit : def.View
          return (
            <section key={def.key} aria-label={def.title} data-focus-card={`ci-${fieldKeyPrefix}-${def.key}`} className={`${CARD} group/card overflow-hidden flex flex-col transition-[border-color,box-shadow] ${def.full ? 'md:col-span-2' : ''} ${solo ? CARD_EDITING : ''}`}>
              <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
                <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0">{def.icon}</span>
                <div className="min-w-0">
                  <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">{def.title}</h3>
                  <div className="text-[12px] text-casevo-text-soft mt-px">{def.sub}</div>
                </div>
                <div className="ml-auto flex gap-1 items-center">
                  {/* Who's editing this card (shared presence — visible while viewing). */}
                  <CardPresence
                    className="mr-1"
                    match={(k) => (def.presenceFields ?? def.fields).some((f) => fk(f) === k) || (SEARCH_KEY[def.key] !== undefined && k === fk(SEARCH_KEY[def.key] as string))}
                  />
                  {solo && <span className="text-[12.5px] text-casevo-text-soft">Editing</span>}
                  {!isEditing && !all && (
                    <button
                      type="button"
                      onClick={() => open(def)}
                      title={`Edit ${def.title}`}
                      aria-label={`Edit ${def.title}`}
                      className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100"
                    >
                      {IcEdit(15)}
                    </button>
                  )}
                </div>
              </div>
              <div className="pt-[2px] px-[18px] pb-4 flex-1"><Body ctx={ctx} /></div>
              {solo && (
                <div className="flex items-center gap-2 justify-end py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
                  <span className="mr-auto text-[12px] text-casevo-text-soft">{saveHint ?? 'Saves as you type · Cancel undoes'}</span>
                  {failed === def.key && <span role="alert" className="text-[12px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
                  <button type="button" className={`${BTN_SEC} ${SM}`} onClick={() => cancel(def)} disabled={saving === def.key}>Cancel</button>
                  <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save(def) }} disabled={saving === def.key}>{IcCheck(13)}{saving === def.key ? 'Saving…' : 'Save'}</button>
                </div>
              )}
            </section>
          )
        })}
        {children}
      </div>

      {!all && footer}

      {/* ── whole-page save bar (Edit all) ── */}
      {all && (
        <div role="region" aria-label="Unsaved changes" className="sticky bottom-4 z-30 mt-4 flex items-center gap-[10px] flex-wrap pl-4 pr-3 py-[10px] rounded-[14px] bg-casevo-surface border border-casevo-mid shadow-[0_12px_32px_rgba(16,42,29,0.16)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
          <span className="w-2 h-2 rounded-full bg-casevo-mid" aria-hidden="true" />
          <span className="text-[13.5px] text-casevo-forest"><b>Editing all sections</b> <span className="text-casevo-text-soft">· saves as you type · Cancel undoes everything since Edit all</span></span>
          {attempted.has('all') && sectionErrors(allFields) && <span role="alert" className="text-[12.5px] text-casevo-danger">Fix the highlighted fields to save.</span>}
          {failed === 'all' && <span role="alert" className="text-[12.5px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
          <div className="ml-auto flex gap-2">
            <button type="button" className={BTN_SEC} onClick={cancelAll} disabled={saving === 'all'}>Cancel</button>
            <button type="button" className={BTN_PRI} onClick={() => { void saveAll() }} disabled={saving === 'all'}>{IcCheck(14)}{saving === 'all' ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientInfoSections
